// "Connect an agent" — surfaces the signed-in user's agent code so they can
// hand it to an AI agent (or any headless tool) to create projects and deploy
// on their behalf. The code is the platform Cognito REFRESH TOKEN for the
// current session: it never exposes the password, and it can be revoked here.
//
// TWO THINGS THIS COMPONENT IS CAREFUL ABOUT, both learned from real dead ends:
//
//  1. Revoking is EXPLICIT. The code used to be revoked by `signOut()`, so
//     signing out of the browser silently killed every agent you'd handed it to
//     — hours later, with an opaque `token refresh failed (HTTP 400)` and
//     nothing linking cause to effect. Sign-out is now local; revocation is the
//     button below, which says exactly what it breaks.
//
//  2. It reads the LIVE session, not sessionStorage directly. sessionStorage is
//     per-tab, so the old `readAgentCode()` returned null in any new tab and the
//     panel showed "No agent code available — sign in first" to a user who was
//     demonstrably signed in.

import {
	Alert,
	Button,
	Card,
	Code,
	CopyButton,
	Divider,
	Group,
	Modal,
	PasswordInput,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { IconAlertTriangle, IconCheck, IconCopy, IconRobot } from "@tabler/icons-react";
import { useState } from "react";
import { PROJECT_PROMPTS } from "../lib/agent-prompts.ts";
import { useSession } from "../session/SessionProvider.tsx";
import { PromptPicker } from "./PromptPicker.tsx";

/** A ready-to-paste bootstrap prompt: the agent fetches the served skill and
 *  follows it; the code rides along as the credential. One paste = handoff. */
function agentPrompt(code: string): string {
	const skillUrl = `${window.location.origin}/skills/s3p-app-builder.md`;
	return [
		`Read ${skillUrl} and follow it to build and ship web apps on s3p for me — you can create projects and deploy them headlessly with the s3p CLI, no browser needed.`,
		"",
		"My agent code (treat it like a password, never commit it):",
		`S3P_AGENT_CODE=${code}`,
	].join("\n");
}

/** `heading={false}` where the PAGE already says "Connect an agent" (the /agent
 *  route) — the card keeps its own header everywhere it's embedded in a flow
 *  that has its own subject, like the sign-up funnel. */
export function ConnectAgent({ heading = true }: { heading?: boolean } = {}) {
	const { client, signIn, revokeAgentAccess } = useSession();
	const [confirmRevoke, setConfirmRevoke] = useState(false);
	const [revoking, setRevoking] = useState(false);

	// The live session — works in every tab, unlike reading sessionStorage.
	const code = client?.getSession()?.refresh_token ?? null;

	async function doRevoke() {
		setRevoking(true);
		try {
			await revokeAgentAccess();
			// The code IS this session's refresh token, so revoking it ends the
			// session too. Send them back through sign-in, which mints a new one.
			signIn("/");
		} finally {
			setRevoking(false);
		}
	}

	return (
		<Card withBorder radius="md" padding="lg">
			{heading && (
				<>
					<Group gap="xs" mb="xs">
						<IconRobot size={20} />
						<Title order={3}>Connect an agent</Title>
					</Group>
					<Text c="dimmed" size="sm" mb="md">
						Hand this <b>agent code</b> to an AI agent (or any headless tool) and it
						can create projects and deploy as you — no browser, no password. Drop it
						into a <Code>.env</Code> as <Code>S3P_AGENT_CODE</Code> and run{" "}
						<Code>s3p login</Code>.
					</Text>
				</>
			)}

			{code ? (
				<Stack gap="sm">
					<PasswordInput
						label="Your agent code"
						description="Treat it like a password. Signing out does NOT revoke it — your agent keeps working."
						value={code}
						readOnly
						onFocus={(e) => e.currentTarget.select()}
					/>
					<Group>
						<CopyButton value={agentPrompt(code)}>
							{({ copied, copy }) => (
								<Button
									color={copied ? "teal" : undefined}
									leftSection={
										copied ? <IconCheck size={16} /> : <IconRobot size={16} />
									}
									onClick={copy}
								>
									{copied ? "Copied" : "Copy prompt for your agent"}
								</Button>
							)}
						</CopyButton>
						<CopyButton value={code}>
							{({ copied, copy }) => (
								<Button
									variant="light"
									color={copied ? "teal" : undefined}
									leftSection={
										copied ? <IconCheck size={16} /> : <IconCopy size={16} />
									}
									onClick={copy}
								>
									{copied ? "Copied" : "Copy code only"}
								</Button>
							)}
						</CopyButton>
						<CopyButton value={`S3P_AGENT_CODE=${code}`}>
							{({ copied, copy }) => (
								<Button
									variant="default"
									leftSection={
										copied ? <IconCheck size={16} /> : <IconCopy size={16} />
									}
									onClick={copy}
								>
									{copied ? "Copied" : "Copy .env line"}
								</Button>
							)}
						</CopyButton>
					</Group>
					<Text size="xs" c="dimmed">
						The prompt tells the agent to read this platform's app-builder guide
						and hands it the code — paste it into Claude Code (or any agent with
						a shell) and it can create and deploy apps for you end to end.
					</Text>

					<Divider my="xs" label="Then tell it what to do" labelPosition="center" />

					<PromptPicker prompts={PROJECT_PROMPTS} maxHeight={190} />

					<Divider my="xs" />

					<Group justify="space-between" align="center">
						<Text size="xs" c="dimmed" style={{ maxWidth: 460 }}>
							Anyone with this code can act as you. Lost it, or pasted it
							somewhere public? Revoke it — every agent using it stops working
							immediately, and you'll get a fresh code when you sign back in.
						</Text>
						<Button
							variant="light"
							color="red"
							leftSection={<IconAlertTriangle size={16} />}
							onClick={() => setConfirmRevoke(true)}
						>
							Revoke agent access
						</Button>
					</Group>

					<Modal
						opened={confirmRevoke}
						onClose={() => setConfirmRevoke(false)}
						title="Revoke agent access?"
						centered
					>
						<Stack gap="md">
							<Text size="sm">
								This immediately breaks <b>every agent or machine</b> holding
								your current code — they'll fail on their next command. Your
								apps keep running; only agent access stops.
							</Text>
							<Text size="sm" c="dimmed">
								You'll be signed out and can copy a new code straight after
								signing in again.
							</Text>
							<Group justify="flex-end">
								<Button variant="default" onClick={() => setConfirmRevoke(false)}>
									Cancel
								</Button>
								<Button color="red" loading={revoking} onClick={() => void doRevoke()}>
									Revoke and sign out
								</Button>
							</Group>
						</Stack>
					</Modal>
				</Stack>
			) : (
				<Alert color="yellow" variant="light" title="No agent code available">
					Sign in first; your agent code is your session's refresh token.
				</Alert>
			)}
		</Card>
	);
}
